// =====================================================================
// PREMIUM CURRENCY SYMBOLS — client layer (SAR ﷼ / EGP ج.م)
// =====================================================================
// The server already sets the canonical `Currency.symbol` (see
// erpnext16_theme/currency.py) so Desk, reports and PDFs render the clean
// glyph natively. This client layer adds the *styled* presentation: it
// wraps the symbol inside the rendered Currency cells with a themed
// <span> (html context — DOM/datagrid only, never plain-text paths) and
// hardens the in-memory symbol caches against any stale value.
//
// Idempotent (frappe._e16_currency_patched) + interval-polled so it
// survives late-loaded Frappe dependencies.
// =====================================================================

const TARGET = {
	SAR: "﷼",   // ﷼
	EGP: "ج.م",
};

// Legacy tokens (longest-first) → currency code.
const LEGACY = [
	["ريال سعودي", "SAR"], ["Saudi Riyal", "SAR"], ["ر.س.", "SAR"], ["ر.س", "SAR"],
	["SAR", "SAR"], ["ريال", "SAR"], ["﷼", "SAR"],
	["جنيه مصري", "EGP"], ["Egyptian Pound", "EGP"], ["ج.م.", "EGP"], ["ج.م", "EGP"],
	["EGP", "EGP"], ["جنيه", "EGP"],
];

function esc(s) {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// One alternation, longest tokens first; ASCII codes get word guards.
const TOKEN_RE = new RegExp(
	LEGACY.map(([tok]) => {
		const e = esc(tok);
		return /^[A-Za-z]+$/.test(tok) ? `(?<![A-Za-z])${e}(?![A-Za-z])` : e;
	}).join("|"),
	"g"
);

// Non-global twin for stateless `.test()` (TOKEN_RE is /g and stateful).
const TOKEN_TEST = new RegExp(TOKEN_RE.source);

function codeForToken(tok) {
	const hit = LEGACY.find(([t]) => t === tok);
	return hit ? hit[1] : null;
}

function span(code) {
	const sym = TARGET[code];
	return `<span class="e16-currency-symbol e16-${code.toLowerCase()}-symbol" aria-label="${code}">${sym}</span>`;
}

// Replace any legacy/target token in a *rendered* string with the styled
// span. Skips strings that already contain our span (no double-wrap) and
// never touches the inside of existing HTML tags.
function decorate(str) {
	if (typeof str !== "string" || !str) return str;
	if (str.indexOf("e16-currency-symbol") !== -1) return str;
	try {
		// Split out HTML tags so we only rewrite text nodes.
		return str.replace(/(<[^>]+>)|([^<]+)/g, (m, tag, text) => {
			if (tag) return tag;
			return text.replace(TOKEN_RE, (tok) => {
				const code = codeForToken(tok);
				return code ? span(code) : tok;
			});
		});
	} catch (e) {
		return str;
	}
}

// --- harden the in-memory symbol caches (defensive; DB is already clean) ---
function normalizeCaches() {
	try {
		const f = window.frappe;
		if (!f) return;
		if (f.boot && f.boot.currency_symbols) {
			["SAR", "EGP"].forEach((c) => {
				if (c in f.boot.currency_symbols) f.boot.currency_symbols[c] = TARGET[c];
			});
		}
		if (window.locals && window.locals[":Currency"]) {
			["SAR", "EGP"].forEach((c) => {
				const row = window.locals[":Currency"][c];
				if (row) row.symbol = TARGET[c];
			});
		}
	} catch (e) {}
}

// --- patch the DOM Currency formatter(s) ---
function patchFormatters() {
	const f = window.frappe;
	if (!f) return false;

	let patchedAny = false;

	const wrapFormatter = (host, key) => {
		if (!host || typeof host[key] !== "function") return;
		if (host[key]._e16_currency) return;
		const orig = host[key];
		const wrapped = function (...args) {
			const out = orig.apply(this, args);
			return decorate(out);
		};
		wrapped._e16_currency = true;
		host[key] = wrapped;
		patchedAny = true;
	};

	// Form/datagrid formatter — renders HTML, so the styled span is safe.
	if (f.form && f.form.formatters) wrapFormatter(f.form.formatters, "Currency");
	// Alias namespace, if this build exposes it.
	if (f.formatters) wrapFormatter(f.formatters, "Currency");

	// meta.get_currency_symbol → return the clean glyph (plain text).
	if (f.meta && typeof f.meta.get_currency_symbol === "function"
		&& !f.meta.get_currency_symbol._e16_currency) {
		const origSym = f.meta.get_currency_symbol;
		const wrappedSym = function (currency) {
			if (currency && TARGET[currency]) return TARGET[currency];
			return origSym.apply(this, arguments);
		};
		wrappedSym._e16_currency = true;
		f.meta.get_currency_symbol = wrappedSym;
		patchedAny = true;
	}

	return patchedAny;
}

function tryPatch() {
	const f = window.frappe;
	if (!f) return false;
	normalizeCaches();
	const ok = patchFormatters();
	if (ok) {
		try { f._e16_currency_patched = true; } catch (e) {}
	}
	// "done" once the form formatters are present & patched.
	return !!(f.form && f.form.formatters && f.form.formatters.Currency
		&& f.form.formatters.Currency._e16_currency);
}

// =====================================================================
// LIVE DOM observer — Number Cards / dashboard widgets / datagrid cells
// rewrite their innerHTML asynchronously AFTER the formatters ran, so a
// bare "ريال / ر.س / SAR / جنيه / ج.م / EGP" (or the plain glyph) can slip
// through. A throttled MutationObserver converts those into the styled
// span the instant they appear.
//
// SAFETY:
//  • We replace TEXT NODES only (never a container's innerHTML) so Frappe's
//    bound events + DOM structure on number cards stay intact.
//  • We only touch elements matching the money selectors AND containing a
//    digit — so the word "ريال" inside ordinary text is never converted.
//  • No infinite loop: the replacement span's own text node is skipped, and
//    a digit/selector-scoped pass is naturally idempotent.
// =====================================================================
const LIVE_SELECTOR = [
	".number-card-value",
	".number-card .widget-content",
	".dt-cell__content",
	".grid-footer-sum",
	".report-summary .summary-value",
	".stat-detail .stat-value",
].join(",");

let observer = null;
let scheduled = false;
const pending = new Set();

function buildSpanEl(code) {
	const el = document.createElement("span");
	el.className = `e16-currency-symbol e16-${code.toLowerCase()}-symbol`;
	el.setAttribute("aria-label", code);
	el.textContent = TARGET[code];
	return el;
}

// Replace currency tokens inside ONE text node with text + span fragments.
function replaceTextNode(node) {
	const text = node.nodeValue;
	if (!text) return;
	TOKEN_RE.lastIndex = 0;
	if (!TOKEN_TEST.test(text)) return;
	const frag = document.createDocumentFragment();
	let last = 0;
	let m;
	TOKEN_RE.lastIndex = 0;
	while ((m = TOKEN_RE.exec(text)) !== null) {
		const tok = m[0];
		const code = codeForToken(tok);
		if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
		frag.appendChild(code ? buildSpanEl(code) : document.createTextNode(tok));
		last = m.index + tok.length;
	}
	if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
	if (node.parentNode) node.parentNode.replaceChild(frag, node);
}

// Walk only the text nodes of a money container; skip our own spans/inputs.
function decorateContainer(el) {
	if (!el || el.nodeType !== 1) return;
	// Require a digit → it's an amount, not stray text containing "ريال".
	if (!/\d/.test(el.textContent || "")) return;
	let walker;
	try {
		walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
			acceptNode(n) {
				const p = n.parentNode;
				if (!p) return NodeFilter.FILTER_REJECT;
				const tag = p.nodeName;
				if (tag === "SCRIPT" || tag === "STYLE" || tag === "TEXTAREA" || tag === "INPUT")
					return NodeFilter.FILTER_REJECT;
				if (p.classList && p.classList.contains("e16-currency-symbol"))
					return NodeFilter.FILTER_REJECT;
				return TOKEN_TEST.test(n.nodeValue || "")
					? NodeFilter.FILTER_ACCEPT
					: NodeFilter.FILTER_REJECT;
			},
		});
	} catch (e) {
		return;
	}
	const nodes = [];
	let cur;
	while ((cur = walker.nextNode())) nodes.push(cur);
	nodes.forEach(replaceTextNode);
}

function scanRoot(root) {
	try {
		if (!root || root.nodeType !== 1) return;
		if (root.matches && root.matches(LIVE_SELECTOR)) decorateContainer(root);
		if (root.querySelectorAll) {
			root.querySelectorAll(LIVE_SELECTOR).forEach(decorateContainer);
		}
	} catch (e) {}
}

function flush() {
	scheduled = false;
	const roots = Array.from(pending);
	pending.clear();
	roots.forEach(scanRoot);
}

function schedule(node) {
	if (!node || (node.nodeType !== 1)) return;
	pending.add(node);
	if (!scheduled) {
		scheduled = true;
		const raf = window.requestAnimationFrame || ((cb) => setTimeout(cb, 16));
		raf(flush);
	}
}

function startObserver() {
	if (observer || !document.body) return;
	observer = new MutationObserver((muts) => {
		for (const m of muts) {
			if (m.addedNodes && m.addedNodes.length) {
				m.addedNodes.forEach((n) => schedule(n.nodeType === 3 ? n.parentNode : n));
			} else if (m.type === "characterData" && m.target) {
				schedule(m.target.parentNode);
			}
		}
	});
	observer.observe(document.body, { childList: true, subtree: true, characterData: true });
	scanRoot(document.body); // initial sweep
}

export const currencyFormatter = {
	TARGET,
	decorate,
	startObserver,
	mount() {
		// Live DOM observer for async Number Cards / datagrid cells.
		if (document.body) {
			startObserver();
		} else {
			document.addEventListener("DOMContentLoaded", startObserver, { once: true });
		}

		// Fast path.
		if (tryPatch()) {
			this._afterReady();
			return;
		}
		// Poll for late-loaded frappe.form.formatters.
		let tries = 0;
		const iv = setInterval(() => {
			tries++;
			if (tryPatch() || tries > 100) {
				clearInterval(iv);
				this._afterReady();
			}
		}, 120);
	},
	_afterReady() {
		// Re-normalise caches once the session boot finishes (covers a boot
		// payload that arrives after our first pass).
		try {
			const $ = window.jQuery || window.$;
			if ($) $(document).one("app_ready", normalizeCaches);
			document.addEventListener("app_ready", normalizeCaches, { once: true });
		} catch (e) {}
	},
};
